/**
 * Botão para solicitar visualização de tela (WebRTC)
 * Exibido no header do chat para atendentes do tenant de suporte
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Monitor, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScreenShareSessionState } from '@/hooks/webrtc/types';

interface ScreenShareButtonProps {
  /** Estado atual da sessão de screen share */
  sessionState: ScreenShareSessionState;
  /** Callback para solicitar visualização */
  onRequestScreenShare: () => void;
  /** Callback para encerrar sessão ativa */
  onEndScreenShare: () => void;
  /** Se true, desabilita o botão */
  disabled?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Botão para solicitar/gerenciar visualização de tela remota
 * 
 * Estados visuais:
 * - idle: Botão normal "Visualizar Tela"
 * - requesting: Loading, aguardando resposta do cliente
 * - connected: Botão verde pulsante, indica sessão ativa
 * - outros estados: desabilitado ou com feedback visual
 */
export function ScreenShareButton({
  sessionState,
  onRequestScreenShare,
  onEndScreenShare,
  disabled = false,
  className,
}: ScreenShareButtonProps) {
  // Determina se está em estado de loading
  const isLoading = sessionState === 'requesting' || sessionState === 'connecting';
  
  // Determina se está conectado (sessão ativa)
  const isConnected = sessionState === 'connected';
  
  // Handler de clique baseado no estado
  const handleClick = () => {
    if (isConnected) {
      onEndScreenShare();
    } else if (sessionState === 'idle') {
      onRequestScreenShare();
    }
  };

  // Texto do tooltip baseado no estado
  const getTooltipText = () => {
    switch (sessionState) {
      case 'idle':
        return 'Solicitar visualização de tela';
      case 'requesting':
        return 'Aguardando resposta do cliente...';
      case 'connecting':
        return 'Estabelecendo conexão...';
      case 'connected':
        return 'Clique para encerrar visualização';
      case 'reconnecting':
        return 'Reconectando...';
      default:
        return 'Visualizar tela';
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={isConnected ? 'default' : 'ghost'}
          size="icon"
          onClick={handleClick}
          disabled={disabled || isLoading}
          className={cn(
            'h-8 w-8 relative',
            // Estado conectado: fundo verde com animação de pulso
            isConnected && 'bg-green-600 hover:bg-green-700 text-white',
            className
          )}
          aria-label={getTooltipText()}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Monitor className="h-4 w-4" />
          )}
          
          {/* Indicador de sessão ativa (pulso) */}
          {isConnected && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{getTooltipText()}</p>
      </TooltipContent>
    </Tooltip>
  );
}
